



import { useCallback, useEffect, useState } from "react";
import { Button, Col, Row, Form } from "react-bootstrap";
import ColumnChart from "../charts/columnChart";
import {
  MONTHS_ARRAY_FULLNAME,
  STATIC_GROUPS,
  TARGET_LABEL,
  WATER_UNITS_1,
  WASTE_UNITS_1,
  ENERGY_UNITS_1,
  WATER_INTENSITY,
} from "../../constants/constants";
import { useAuth } from "../../context/authProvider";
import { getRoundedValue } from "../../utils/helpers";
import useStore from "../../hooks/useStore";

const DashboardHistogram = ({ title, group, reportType }) => {
  const filterTypeOptions = [
    { value: "monthly", label: "Monthly" },
    { value: "yearly_detailed", label: "Yearly (Monthly breakdown)" },
    { value: "yearly_summary", label: "Yearly (Summary)" },
  ];
  
  const {
    companyStore: { companyUnit },
  } = useStore();

  const { showAlert } = useAuth();
  const [unit, setUnit] = useState("");
  const [data, setData] = useState();
  const [rawData, setRawData] = useState(); 
  // Water Group I filter state
  const [waterGroupIFields, setWaterGroupIFields] = useState([]);
  const [selectedWaterGroupIField, setSelectedWaterGroupIField] = useState("");
  const [waterGroupIValue, setWaterGroupIValue] = useState(null);
  const [waterGroupIDetailedData, setWaterGroupIDetailedData] = useState(null); // NEW: for yearly_detailed
  // AQI Group E filter state
  const [aqiGroupEFields, setAqiGroupEFields] = useState([]);
  const [selectedAqiGroupEField, setSelectedAqiGroupEField] = useState("");
  const [aqiGroupEValue, setAqiGroupEValue] = useState(null);
  const [aqiGroupEDetailedData, setAqiGroupEDetailedData] = useState(null); // NEW: for yearly_detailed
  const [graphMode, setGraphMode] = useState("separate");
  const [targetLines, setTargetLines] = useState({});
  const [permissibleLimit, setPermissibleLimit] = useState(null);
  const [hasInitialFetch, setHasInitialFetch] = useState(false); // NEW: track first fetch
  
  const [selectedSubGroup, setSelectedSubGroup] = useState("");

  const [waterFilter, setWaterFilter] = useState({
    month: "",
    fromMonth: "",
    toMonth: "",
    year: new Date().getFullYear(),
    type: "",
  });

  const [head, setHead] = useState([
    MONTHS_ARRAY_FULLNAME[new Date().getMonth()],
  ]);

  const {
    companyStore: { units: companyUnits },
  } = useStore();

  const getSubGroupOptions = () => {
    if (reportType !== "waste") return [];
    
    const wasteSubGroups = {
      B: [
        { value: "B", label: "All Waste Diverted from Disposal" },
        { value: "B1", label: "Hazardous Waste" },
        { value: "B2", label: "Non - Hazardous Waste" },
        { value: "B3", label: "E-waste" }
      ],
      C: [
        { value: "C", label: "All Waste directed to Disposal" },
        { value: "C1", label: "Hazardous Waste" },
        { value: "C2", label: "Non - Hazardous" }
      ]
    };
    
    return wasteSubGroups[group] || [];
  };

  const shouldShowSubGroupDropdown = () => {
    return reportType === "waste" && (group === "B" || group === "C");
  };

  useEffect(() => {
    if (shouldShowSubGroupDropdown()) {
      const subGroupOptions = getSubGroupOptions();
      if (subGroupOptions.length > 0 && !selectedSubGroup) {
        setSelectedSubGroup(subGroupOptions[0].value);
      }
    }
  }, [group, reportType]);

  const getUnitsForReportType = () => {
    switch (reportType) {
      case "water":
        return ["KL", "ML", "M3"];
      case "waste":
        return ["MT", "Kg", "L", "M3"];
      case "energy":
        return ["Gj", "Mj", "J", "KWh"];
      case "aqi":
        return ["tCO2e"];
      case "biodiversity":
        return ["Nos", "Acre", "KM3"];
      default:
        return [];
    }
  };

  const shouldShowUnitDropdown = (reportType, group) => {
    if (reportType === "water" && (group === "D" || group === "E" || group === "I")) return false;
    if (reportType === "waste") return false;
    if (reportType === "energy" && group === "Z") return false;
    if (reportType === "aqi" && (group === "J" || group === "E")) return false;
    if (reportType === "biodiversity" && group === "B") return false;
    return true;
  };

  const availableUnits = getUnitsForReportType();

  const onChangeHandler = (event) => {
    const { name, value } = event.target;
    setWaterFilter((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset fieldName filter when type, month, fromMonth, or toMonth changes
    if (["type", "month", "fromMonth", "toMonth"].includes(name)) {
      setSelectedWaterGroupIField("");
      setSelectedAqiGroupEField("");
    }

    // For unit dropdown, set the unit name, not id
    if (name === "unit") {
      setUnit(value); // value is the unit name from dropdown
    }
  };

  const onSubGroupChangeHandler = (event) => {
    setSelectedSubGroup(event.target.value);
  };

  const onSearchHandler = (e) => {
    e.preventDefault();
    
    if (shouldShowSubGroupDropdown() && !selectedSubGroup) {
      showAlert?.("Please select a waste category before searching", false);
      return;
    }

    if (waterFilter.type === "monthly") {
      setHead([waterFilter.month]);
    } else if (waterFilter.type === "yearly_detailed") {
      setHead([`${waterFilter.fromMonth} - ${waterFilter.toMonth}`]);
    } else if (waterFilter.type === "yearly_summary") {
      const currentYear = parseInt(waterFilter.year);
      const previousYear = currentYear - 1;
      setHead([previousYear.toString(), currentYear.toString()]);
    }
    fetchData(waterFilter);
  };

  const fetchData = useCallback(
    async (filter) => {
      const selectedYear = parseInt(filter.year);
      let selectedMonths = [];
      if (filter.type === "monthly") {
        selectedMonths = [filter.month];
      } else if (filter.type === "yearly_detailed") {
        selectedMonths = MONTHS_ARRAY_FULLNAME.slice(
          MONTHS_ARRAY_FULLNAME.indexOf(filter.fromMonth),
          MONTHS_ARRAY_FULLNAME.indexOf(filter.toMonth) + 1
        );
      }

      let groupsToFetch;
      if (shouldShowSubGroupDropdown()) {
        if (selectedSubGroup === group) {
          const reportGroups = STATIC_GROUPS[reportType] || {};
          const childGroups = Object.keys(reportGroups).filter(
            (g) => g !== group && g.startsWith(group)
          );
          groupsToFetch = childGroups.length > 0 ? childGroups : [group];
        } else {
          groupsToFetch = [selectedSubGroup];
        }
      } else {
        const reportGroups = STATIC_GROUPS[reportType] || {};
        const childGroups = Object.keys(reportGroups).filter(
          (g) => g !== group && g.startsWith(group)
        );
        groupsToFetch = childGroups.length > 0 ? childGroups : [group];
      }

      try {
        // NEW: For Water Group I yearly_detailed, fetch data for each month
        if (reportType === "water" && group === "I" && filter.type === "yearly_detailed") {
          const monthlyWaterData = [];
          
          for (const month of selectedMonths) {
            let paramsObj = {
              type: "monthly",
              year: selectedYear,
              month: month,
              group: group,
              reportType,
              companyUnit,
              selectedUnit: unit || ""
            };
            const params = new URLSearchParams(paramsObj).toString();

            const res = await fetch(
              `${process.env.REACT_APP_BASE_URL}company/dashboard?${params}`,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${
                    JSON.parse(localStorage.getItem("access-token"))?.token
                  }`,
                },
              }
            );

            const result = await res.json();
            
            if (result.status && Array.isArray(result.data?.water)) {
              monthlyWaterData.push({
                month: month,
                water: result.data.water,
                unit: result.data.unit
              });
            }
          }

          // Set fields from first month's data
          if (monthlyWaterData.length > 0 && monthlyWaterData[0].water) {
            const fields = monthlyWaterData[0].water.map(w => ({
              id: w.id,
              fieldName: w.fieldName,
              unit: w.unit || monthlyWaterData[0].unit || unit
            }));
            setWaterGroupIFields(fields);
            setHasInitialFetch(true);
          }

          // Store monthly data for processing
          setWaterGroupIDetailedData(monthlyWaterData);
          return;
        }

        // NEW: For AQI Group E yearly_detailed, fetch data for each month
        if (reportType === "aqi" && group === "E" && filter.type === "yearly_detailed") {
          const monthlyAqiData = [];
          
          for (const month of selectedMonths) {
            let paramsObj = {
              type: "monthly",
              year: selectedYear,
              month: month,
              group: group,
              reportType,
              companyUnit,
              selectedUnit: unit || ""
            };
            const params = new URLSearchParams(paramsObj).toString();

            const res = await fetch(
              `${process.env.REACT_APP_BASE_URL}company/dashboard?${params}`,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${
                    JSON.parse(localStorage.getItem("access-token"))?.token
                  }`,
                },
              }
            );

            const result = await res.json();
            
            if (result.status && Array.isArray(result.data?.aqi)) {
              monthlyAqiData.push({
                month: month,
                aqi: result.data.aqi,
                unit: result.data.unit
              });
            }
          }

          if (monthlyAqiData.length > 0 && monthlyAqiData[0].aqi) {
            const fields = monthlyAqiData[0].aqi.map(a => ({
              id: a.id,
              fieldName: a.fieldName,
              unit: a.unit || monthlyAqiData[0].unit || unit
            }));
            setAqiGroupEFields(fields);
            setHasInitialFetch(true);
          }

          setAqiGroupEDetailedData(monthlyAqiData);
          return;
        }

        // Original fetch logic for other cases
        const allGroupData = [];
        for (const g of groupsToFetch) {
          let paramsObj = {
            type: filter.type,
            year: selectedYear,
            group: g,
            reportType,
            companyUnit,
            selectedUnit: unit || ""
          };
          if (filter.type === "monthly") {
            paramsObj.month = filter.month;
          } else if (filter.type === "yearly_detailed") {
            paramsObj.fromMonth = filter.fromMonth;
            paramsObj.toMonth = filter.toMonth;
          }
          const params = new URLSearchParams(paramsObj).toString();

          const res = await fetch(
            `${process.env.REACT_APP_BASE_URL}company/dashboard?${params}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${
                  JSON.parse(localStorage.getItem("access-token"))?.token
                }`,
              },
            }
          );

          const result = await res.json();
          
          console.log("Fetched data for group", result);
          if (!result.status) {
            showAlert?.(result.message || "Backend error occurred", false);
            return;
          }

          if (result.status && Array.isArray(result.data?.chartData)) {
            allGroupData.push(...result.data.chartData);
            
            if (result.data.chartData && result.data.chartData.length > 0) {
              if (!unit || unit === "") {
                const backendUnit = result.data.chartData[0].unit;
                setUnit(backendUnit && backendUnit.trim() ? backendUnit : "");
              }
              setRawData(result.data.chartData);
            } else {
              setRawData([]);
            }
            if (result.data?.limit && !permissibleLimit)
              setPermissibleLimit({ limit: result.data.limit });
          }

          // Water Group I filter setup (monthly only)
          if (reportType === "water" && group === "I" && filter.type === "monthly" && Array.isArray(result.data?.water)) {
            setWaterGroupIFields(result.data.water.map(w => ({
              id: w.id,
              fieldName: w.fieldName,
              value: w.value,
              unit: w.unit || result.data.unit || unit
            })));
            setHasInitialFetch(true);
          }

          // AQI Group E filter setup (monthly only)
          if (reportType === "aqi" && group === "E" && filter.type === "monthly" && Array.isArray(result.data?.aqi)) {
            setAqiGroupEFields(result.data.aqi.map(a => ({
              id: a.id,
              fieldName: a.fieldName,
              value: a.value,
              unit: a.unit || result.data.unit || unit
            })));
            setHasInitialFetch(true);
          }

          // AQI Group E filter setup (yearly_summary)
          if (reportType === "aqi" && group === "E" && filter.type === "yearly_summary" && Array.isArray(result.data?.aqi)) {
            // Add year info to each field
            const year = paramsObj.year;
            setAqiGroupEFields(result.data.aqi.map(a => ({
              id: a.id,
              fieldName: a.fieldName,
              value: a.value,
              unit: a.fieldUnit || result.data.unit || unit,
              year: year
            })));
            setHasInitialFetch(true);
          }
        }

        const merged = {};
        allGroupData.forEach((item) => {
          const itemUnit = item.data && item.data.length > 0 && item.data[0].unit ? item.data[0].unit : undefined;
          if (!merged[item.name]) {
            merged[item.name] = {
              ...item,
              unit: itemUnit,
              data: item.data.map((d) => ({
                value: getRoundedValue(d.prevValue),
                target: getRoundedValue(d.target),
                unit: d.unit,
              })),
            };
          } else {
            merged[item.name].data = merged[item.name].data.map((val, i) => ({
              value:
                getRoundedValue(val.value) +
                getRoundedValue(item.data[i]?.prevValue || 0),
              target:
                getRoundedValue(val.target) +
                getRoundedValue(item.data[i]?.target || 0),
              unit: item.data[i]?.unit || val.unit,
            }));
            if (!merged[item.name].unit && itemUnit) {
              merged[item.name].unit = itemUnit;
            }
          }
        });

        let finalData = Object.values(merged);
        let categories = selectedMonths;
        const targetSumLines = {};

        if (filter.type === "yearly_summary") {
          const currentYear = parseInt(filter.year);
          const previousYear = currentYear - 1;
          categories = [previousYear.toString(), currentYear.toString()];

          const yearlyData = {};
          const yearlyUnits = {};
          finalData.forEach((item) => {
            const year = parseInt(item.name.match(/\d{4}/)?.[0]);
            if (!isNaN(year) && item.data.length > 0) {
              const dataPoint = item.data[0];
              yearlyData[year] = typeof dataPoint === "object" ? dataPoint.value : dataPoint;
              yearlyUnits[year] = typeof dataPoint === "object" ? dataPoint.unit : (item.unit || null);
              
              const totalTarget = item.data.reduce(
                (sum, d) => sum + getRoundedValue(d.target || 0),
                0
              );
              targetSumLines[year] = totalTarget;
            }
          });

          finalData = [{
            name: getCurrentTitle(),
            data: [
              { value: yearlyData[previousYear] || 0, unit: yearlyUnits[previousYear] || null },
              { value: yearlyData[currentYear] || 0, unit: yearlyUnits[currentYear] || null }
            ]
          }];
        } else if (graphMode === "combined" && filter.type !== "yearly_summary") {
          finalData = finalData.map((item) => {
            const year = parseInt(item.name.match(/\d{4}/)?.[0]);
            const totalVal = item.data.reduce(
              (sum, d) => sum + getRoundedValue(d.value || 0),
              0
            );
            const totalTarget = item.data.reduce(
              (sum, d) => sum + getRoundedValue(d.target || 0),
              0
            );

            if (!isNaN(year)) {
              targetSumLines[year] = totalTarget;
            }

            const combinedUnit = item.data.length > 0 ? item.data[0].unit || item.unit || unit : unit;
            
            return {
              ...item,
              data: [{ value: totalVal, unit: combinedUnit }]
            };
          });

          categories = [
            filter.type === "yearly_detailed"
              ? `${filter.fromMonth.slice(0, 3)}-${filter.toMonth.slice(0, 3)}`
              : filter.month,
          ];
        } else {
          finalData.forEach((item) => {
            const year = parseInt(item.name.match(/\d{4}/)?.[0]);
            if (!isNaN(year)) {
              const totalTarget = item.data
                .map((d) => getRoundedValue(d.target || 0))
                .reduce((sum, v) => sum + v, 0);
              targetSumLines[year] = totalTarget;
            }
          });
        }

        setTargetLines(targetSumLines);
        setData(finalData);
        setHead(categories);
      } catch (err) {
        console.error(err);
        showAlert?.("Error fetching data", false);
      }
    },
    [
      group,
      reportType,
      unit,
      selectedSubGroup,
      permissibleLimit,
      showAlert,
      graphMode,
    ]
  );

  const convertChartData = (newUnit) => {
    if (!rawData || !Array.isArray(rawData) || unit === newUnit) {
      setUnit(newUnit);
      const updated = (rawData || []).map(item => ({
        ...item,
        units: Array.isArray(item.data) ? item.data.map(() => newUnit) : [newUnit],
        unit: newUnit
      }));
      setData(updated);
      return;
    }

    const CONVERSION_MAPS = {
      water: {
        KL: { M3: v => v * 1, ML: v => v / 1000 },
        M3: { KL: v => v * 1, ML: v => v / 1000 },
        ML: { KL: v => v * 1000, M3: v => v * 1000 },
      },
      waste: {
        MT: { Kg: v => v * 1000, Nos: v => v, M3: v => v * 1, L: v => v * 1000 },
        Kg: { MT: v => v / 1000, Nos: v => v, L: v => v / 1 },
        Nos: { MT: v => v, Kg: v => v },
        L: { M3: v => v / 1000, MT: v => v / 1000, Kg: v => v * 1 },
        M3: { L: v => v * 1000, MT: v => v * 1 },
      },
      energy: {
        Gj: { Mj: v => v * 1000, J: v => v * 1e9, KWh: v => v * 277.778 },
        Mj: { Gj: v => v / 1000, J: v => v * 1e6, KWh: v => v * 0.277778 },
        J: { Gj: v => v / 1e9, Mj: v => v / 1e6, KWh: v => v / 3.6e6 },
        KWh: { Gj: v => v * 0.0036, Mj: v => v / 0.277778, J: v => v * 3.6e6 },
        MT: { Gj: v => v, Mj: v => v, J: v => v, KWh: v => v, M3: v => v * 1 },
        M3: { MT: v => v * 1 },
      },
      aqi: {
        MT: { tCO2e: v => v },
        tCO2e: { MT: v => v },
      },
      biodiversity: {
        Nos: { Acre: v => v, KM3: v => v },
        Acre: { Nos: v => v, KM3: v => v },
        KM3: { Nos: v => v, Acre: v => v },
      },
    };

    const map = CONVERSION_MAPS[reportType];
    if (!map) {
      alert(`No conversion map found for report type: ${reportType}`);
      return;
    }

    const sourceData = Array.isArray(data) ? data : [];
    const converted = sourceData.map(item => {
      const convertedData = item.data.map((d, idx) => {
        let inputVal;
        let fromUnit = Array.isArray(item.units) && item.units[idx] ? item.units[idx] : item.unit || unit;
        if (typeof d === "number") {
          inputVal = d;
        } else if (d && typeof d === "object") {
          inputVal = d.value !== undefined ? d.value : d.prevValue;
          if (d.unit) fromUnit = d.unit;
        } else {
          inputVal = 0;
        }
        if (!map[fromUnit] || !map[fromUnit][newUnit]) {
          console.warn(`[UnitConversion] ${fromUnit} cannot be converted to ${newUnit}. Value left unchanged.`);
          return inputVal;
        }
        const outputVal = map[fromUnit][newUnit](inputVal);
        return outputVal;
      });
      return {
        ...item,
        unit: newUnit,
        data: convertedData,
        units: convertedData.map(() => newUnit)
      };
    });

    setUnit(newUnit);
    setData(converted);
  };

  useEffect(() => {
    fetchData(waterFilter);
  }, [companyUnits]);

  // NEW: Process Water Group I data for yearly_detailed
  useEffect(() => {
    if (reportType === "water" && group === "I" && waterFilter.type === "yearly_detailed" && waterGroupIDetailedData) {
      if (!selectedWaterGroupIField) {
        // No field selected - show total of all fields
        if (graphMode === "combined") {
          // Combined: show year total
          const totalValue = waterGroupIDetailedData.reduce((sum, monthData) => {
            const monthTotal = monthData.water.reduce((mSum, field) => mSum + (field.value || 0), 0);
            return sum + monthTotal;
          }, 0);
          
          setWaterGroupIValue({
            name: "Total (All Fields)",
            data: [totalValue],
            unit: waterGroupIDetailedData[0]?.unit || unit,
            categories: [`${waterFilter.fromMonth.slice(0, 3)}-${waterFilter.toMonth.slice(0, 3)}`]
          });
        } else {
          // Separate: show each month's total
          const monthlyTotals = waterGroupIDetailedData.map(monthData => ({
            month: monthData.month,
            value: monthData.water.reduce((sum, field) => sum + (field.value || 0), 0)
          }));
          
          setWaterGroupIValue({
            name: "Total (All Fields)",
            data: monthlyTotals.map(m => m.value),
            unit: waterGroupIDetailedData[0]?.unit || unit,
            categories: monthlyTotals.map(m => m.month)
          });
        }
      } else {
        // Specific field selected
        const selectedField = waterGroupIFields.find(f => f.id === selectedWaterGroupIField);
        
        if (graphMode === "combined") {
          // Combined: show year total for selected field
          const totalValue = waterGroupIDetailedData.reduce((sum, monthData) => {
            const fieldData = monthData.water.find(f => f.fieldName === selectedField?.fieldName);
            return sum + (fieldData?.value || 0);
          }, 0);
          
          setWaterGroupIValue({
            name: selectedField?.fieldName || "Selected Field",
            data: [totalValue],
            unit: selectedField?.unit || unit,
            categories: [`${waterFilter.fromMonth.slice(0, 3)}-${waterFilter.toMonth.slice(0, 3)}`]
          });
        } else {
          // Separate: show each month's value for selected field
          const monthlyValues = waterGroupIDetailedData.map(monthData => {
            const fieldData = monthData.water.find(f => f.fieldName === selectedField?.fieldName);
            return {
              month: monthData.month,
              value: fieldData?.value || 0
            };
          });
          
          setWaterGroupIValue({
            name: selectedField?.fieldName || "Selected Field",
            data: monthlyValues.map(m => m.value),
            unit: selectedField?.unit || unit,
            categories: monthlyValues.map(m => m.month)
          });
        }
      }
    } else if (reportType === "water" && group === "I" && waterFilter.type === "monthly" && selectedWaterGroupIField && waterGroupIFields.length > 0) {
      // Monthly mode - single value
      const selectedFieldObj = waterGroupIFields.find(f => f.id === selectedWaterGroupIField);
      if (selectedFieldObj) {
        setWaterGroupIValue({
          name: selectedFieldObj.fieldName,
          data: [selectedFieldObj.value],
          unit: selectedFieldObj.unit,
          categories: [waterFilter.month]
        });
      }
    } else if (reportType === "water" && group === "I" && waterFilter.type === "monthly" && !selectedWaterGroupIField && waterGroupIFields.length > 0) {
      // Monthly mode - no field selected, show total
      const totalValue = waterGroupIFields.reduce((sum, field) => sum + (field.value || 0), 0);
      setWaterGroupIValue({
        name: "Total (All Fields)",
        data: [totalValue],
        unit: waterGroupIFields[0]?.unit || unit,
        categories: [waterFilter.month]
      });
    } else {
      setWaterGroupIValue(null);
    }
  }, [selectedWaterGroupIField, waterGroupIFields, waterGroupIDetailedData, reportType, group, graphMode, waterFilter.type, waterFilter.fromMonth, waterFilter.toMonth, waterFilter.month, unit]);

  // NEW: Process AQI Group E data for yearly_detailed
  useEffect(() => {
    if (reportType === "aqi" && group === "E" && waterFilter.type === "yearly_detailed" && aqiGroupEDetailedData) {
      if (!selectedAqiGroupEField) {
        // No field selected - show total
        if (graphMode === "combined") {
          const totalValue = aqiGroupEDetailedData.reduce((sum, monthData) => {
            const monthTotal = monthData.aqi.reduce((mSum, field) => mSum + (field.value || 0), 0);
            return sum + monthTotal;
          }, 0);
          
          setAqiGroupEValue({
            name: "Total (All Fields)",
            data: [totalValue],
            unit: aqiGroupEDetailedData[0]?.unit || unit,
            categories: [`${waterFilter.fromMonth.slice(0, 3)}-${waterFilter.toMonth.slice(0, 3)}`]
          });
        } else {
          const monthlyTotals = aqiGroupEDetailedData.map(monthData => ({
            month: monthData.month,
            value: monthData.aqi.reduce((sum, field) => sum + (field.value || 0), 0)
          }));
          
          setAqiGroupEValue({
            name: "Total (All Fields)",
            data: monthlyTotals.map(m => m.value),
            unit: aqiGroupEDetailedData[0]?.unit || unit,
            categories: monthlyTotals.map(m => m.month)
          });
        }
      } else {
        const selectedField = aqiGroupEFields.find(f => f.id === selectedAqiGroupEField);
        
        if (graphMode === "combined") {
          const totalValue = aqiGroupEDetailedData.reduce((sum, monthData) => {
            const fieldData = monthData.aqi.find(f => f.fieldName === selectedField?.fieldName);
            return sum + (fieldData?.value || 0);
          }, 0);
          
          setAqiGroupEValue({
            name: selectedField?.fieldName || "Selected Field",
            data: [totalValue],
            unit: selectedField?.unit || unit,
            categories: [`${waterFilter.fromMonth.slice(0, 3)}-${waterFilter.toMonth.slice(0, 3)}`]
          });
        } else {
          const monthlyValues = aqiGroupEDetailedData.map(monthData => {
            const fieldData = monthData.aqi.find(f => f.fieldName === selectedField?.fieldName);
            return {
              month: monthData.month,
              value: fieldData?.value || 0
            };
          });
          
          setAqiGroupEValue({
            name: selectedField?.fieldName || "Selected Field",
            data: monthlyValues.map(m => m.value),
            unit: selectedField?.unit || unit,
            categories: monthlyValues.map(m => m.month)
          });
        }
      }
    } else if (reportType === "aqi" && group === "E" && waterFilter.type === "monthly" && selectedAqiGroupEField && aqiGroupEFields.length > 0) {
      const selectedFieldObj = aqiGroupEFields.find(f => f.id === selectedAqiGroupEField);
      if (selectedFieldObj) {
        setAqiGroupEValue({
          name: selectedFieldObj.fieldName,
          data: [selectedFieldObj.value],
          unit: selectedFieldObj.unit,
          categories: [waterFilter.month]
        });
      }
    } else if (reportType === "aqi" && group === "E" && waterFilter.type === "monthly" && !selectedAqiGroupEField && aqiGroupEFields.length > 0) {
      const totalValue = aqiGroupEFields.reduce((sum, field) => sum + (field.value || 0), 0);
      setAqiGroupEValue({
        name: "Total (All Fields)",
        data: [totalValue],
        unit: aqiGroupEFields[0]?.unit || unit,
        categories: [waterFilter.month]
      });
    } else {
      setAqiGroupEValue(null);
    }
  }, [selectedAqiGroupEField, aqiGroupEFields, aqiGroupEDetailedData, reportType, group, graphMode, waterFilter.type, waterFilter.fromMonth, waterFilter.toMonth, waterFilter.month, unit]);

  const getYaxisLines = () => {
    const yLines = [];

    if (permissibleLimit) {
      let ctoValue = getRoundedValue(permissibleLimit.limit);
      let yValue = ctoValue;
      if (waterFilter.type === "yearly_detailed" && waterFilter.toMonth) {
        const toIdx = MONTHS_ARRAY_FULLNAME.indexOf(waterFilter.toMonth);
        if (toIdx >= 0) {
          const monthsCount = toIdx + 1;
          ctoValue = getRoundedValue(permissibleLimit.limit * monthsCount);
          yValue = ctoValue;
        }
      }
      yLines.push({
        y: yValue || 0,
        borderColor: "#FF4560",
        label: {
          borderColor: "#FF4560",
          style: { color: "#fff", background: "#FF4560" },
          text: `cto : ${Number(ctoValue).toFixed(2)} | ${getCurrentTitle()}`,
          position: "right",
          align: "right",
          offsetX: 120,
          overflow: "visible",
          width: 500,
        },
      });
    }

    if (waterFilter.type === "yearly_detailed" && graphMode === "combined") {
      Object.entries(targetLines || {}).forEach(([yearStr, val], index) => {
        const year = parseInt(yearStr);
        if (isNaN(year)) return;

        const color = index === 0 ? "#FCD163" : "#EF8083";

        yLines.push({
          y: getRoundedValue(val),
          borderColor: color,
          label: {
            borderColor: color,
            style: { color: "#000", background: color },
            text: `${year} YTD Target : ${getRoundedValue(val)} ${unit}`,
          },
        });
      });
    }

    return yLines;
  };

  const getMaxYValue = () => {
    const targetMax = Math.max(
      ...Object.values(targetLines || {}).map((val) => getRoundedValue(val)),
      0
    );
    const permissibleMax = getRoundedValue(permissibleLimit?.limit) || 0;
    return Math.max(targetMax, permissibleMax) * 1.2;
  };

  const getCurrentTitle = () => {
    if (shouldShowSubGroupDropdown() && selectedSubGroup) {
      const subGroupOptions = getSubGroupOptions();
      const selectedOption = subGroupOptions.find(option => option.value === selectedSubGroup);
      return selectedOption ? selectedOption.label : title;
    }
    return title;
  };

  return (
    <div className="card dashboard-histogram h-100" key={title}>
      <div className="card-body">
        <div className="w-100 d-flex justify-content-between">
          <h6 className="mb-3 text-muted">{getCurrentTitle()}</h6>
        </div>

        <div className="dashboard-histogram-chart">
          <div className="dashboard-chart-grid mb-4">
            <Row>
              <Col lg="3" sm="3">
                <Form.Label>Filter Type</Form.Label>
                <Form.Select
                  name="type"
                  value={waterFilter.type}
                  onChange={onChangeHandler}
                >
                  <option value="">Select Type</option>
                  {filterTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              {shouldShowSubGroupDropdown() && (
                <Col lg="3" sm="3">
                  <Form.Label>Waste Category</Form.Label>
                  <Form.Select
                    value={selectedSubGroup}
                    onChange={onSubGroupChangeHandler}
                  >
                    <option value="">Select Category</option>
                    {getSubGroupOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.value} - {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  {shouldShowSubGroupDropdown() && !selectedSubGroup && (
                    <small className="text-danger">Category selection is required</small>
                  )}
                </Col>
              )}

              {shouldShowUnitDropdown(reportType, group) && (
                <Col lg="3" sm="3">
                  <Form.Label>Unit</Form.Label>
                  <Form.Select
                    name="unit"
                    value={unit}
                    onChange={onChangeHandler}
                  >
                    <option value="">Select Unit</option>
                    {availableUnits.map((unitName) => (
                      <option key={unitName} value={unitName}>
                        {unitName}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              )}



              {/* Always show fieldName filter for Water Group I and AQI Group E if fields exist */}
              {reportType === "water" && group === "I" && waterGroupIFields.length > 0 && (
                <Col lg="3" sm="3">
                  <Form.Label>Field Name (Optional)</Form.Label>
                  <Form.Select
                    value={selectedWaterGroupIField}
                    onChange={e => setSelectedWaterGroupIField(e.target.value)}
                  >
                    <option value="">All Fields (Total)</option>
                    {waterGroupIFields.map(f => (
                      <option key={f.id} value={f.id}>{f.fieldName}</option>
                    ))}
                  </Form.Select>
                </Col>
              )}

              {reportType === "aqi" && group === "E" && aqiGroupEFields.length > 0 && (
                <Col lg="3" sm="3">
                  <Form.Label>Field Name (Optional)</Form.Label>
                  <Form.Select
                    value={selectedAqiGroupEField}
                    onChange={e => setSelectedAqiGroupEField(e.target.value)}
                  >
                    <option value="">All Fields (Total)</option>
                    {aqiGroupEFields.map(f => (
                      <option key={f.id} value={f.id}>{f.fieldName}</option>
                    ))}
                  </Form.Select>
                </Col>
              )}

              {waterFilter.type === "monthly" && (
                <Col lg="3" sm="3">
                  <Form.Label>Month</Form.Label>
                  <Form.Select
                    name="month"
                    value={waterFilter.month}
                    onChange={onChangeHandler}
                  >
                    <option value="">Select Month</option>
                    {MONTHS_ARRAY_FULLNAME.map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              )}

              {waterFilter.type === "yearly_detailed" && (
                <>
                  <Col lg="3" sm="3">
                    <Form.Label>Year</Form.Label>
                    <Form.Select
                      name="year"
                      value={waterFilter.year}
                      onChange={onChangeHandler}
                    >
                      {Array.from({ length: 20 })
                        .map((_, i) => new Date().getFullYear() - i)
                        .map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                    </Form.Select>
                  </Col>
                  <Col lg="3" sm="3">
                    <Form.Label>From Month</Form.Label>
                    <Form.Select
                      name="fromMonth"
                      value={waterFilter.fromMonth}
                      onChange={onChangeHandler}
                    >
                      <option value="">Select</option>
                      {MONTHS_ARRAY_FULLNAME.map((month) => (
                        <option key={month} value={month}>
                          {month}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col lg="3" sm="3">
                    <Form.Label>To Month</Form.Label>
                    <Form.Select
                      name="toMonth"
                      value={waterFilter.toMonth}
                      onChange={onChangeHandler}
                    >
                      <option value="">Select</option>
                      {MONTHS_ARRAY_FULLNAME.map((month) => (
                        <option key={month} value={month}>
                          {month}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col lg="3" sm="3">
                    <Form.Label>Graph Mode</Form.Label>
                    <Form.Select
                      value={graphMode}
                      onChange={(e) => setGraphMode(e.target.value)}
                    >
                      <option value="separate">Separate</option>
                      <option value="combined">Combined</option>
                    </Form.Select>
                  </Col>
                </>
              )}

              {waterFilter.type === "yearly_summary" && (
                <Col lg="3" sm="3">
                  <Form.Label>Year</Form.Label>
                  <Form.Select
                    name="year"
                    value={waterFilter.year}
                    onChange={onChangeHandler}
                  >
                    {Array.from({ length: 20 })
                      .map((_, i) => new Date().getFullYear() - i)
                      .map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                  </Form.Select>
                </Col>
              )}
            </Row>

            <div className="dashboardSearchBtn">
              <Button 
                onClick={onSearchHandler}
                disabled={shouldShowSubGroupDropdown() && !selectedSubGroup}
                variant={shouldShowSubGroupDropdown() && !selectedSubGroup ? "secondary" : "primary"}
              >
                Search
              </Button>
              {shouldShowSubGroupDropdown() && !selectedSubGroup && (
                <small className="text-muted d-block mt-1">
                  Please select a waste category to enable search
                </small>
              )}
            </div>
          </div>

          {/* Chart display for Water Group I and AQI Group E */}
          {reportType === "water" && group === "I" && waterGroupIValue ? (
            <ColumnChart
              data={[{
                name: waterGroupIValue.name,
                data: waterGroupIValue.data,
                units: waterGroupIValue.data.map(() => waterGroupIValue.unit)
              }]}
              categories={waterGroupIValue.categories}
              title={waterGroupIValue.name}
              hover={waterGroupIValue.name}
              unit={waterGroupIValue.unit}
              yaxis={getYaxisLines()}
              height={350}
              max={getMaxYValue()}
              reportType={reportType}
              filterType={waterFilter.type}
              currentYear={parseInt(waterFilter.year)}
            />
          ) : reportType === "water" && group === "I" && waterFilter.type === "yearly_summary" && hasInitialFetch && waterGroupIFields.length > 0 ? (
            <ColumnChart
              data={[{
                name: selectedWaterGroupIField
                  ? waterGroupIFields.find(f => f.id === selectedWaterGroupIField)?.fieldName || "Selected Field"
                  : "Total (All Fields)",
                data: (() => {
                  // Find yearly summary values for selected field or total
                  const prevYear = parseInt(waterFilter.year) - 1;
                  const currYear = parseInt(waterFilter.year);
                  if (selectedWaterGroupIField) {
                    // Find value for selected field for each year
                    const prevObj = waterGroupIFields.find(f => f.id === selectedWaterGroupIField && f.year === prevYear);
                    const currObj = waterGroupIFields.find(f => f.id === selectedWaterGroupIField && f.year === currYear);
                    return [prevObj?.value || 0, currObj?.value || 0];
                  } else {
                    // Sum all fields for each year
                    const prevTotal = waterGroupIFields.filter(f => f.year === prevYear).reduce((sum, f) => sum + (f.value || 0), 0);
                    const currTotal = waterGroupIFields.filter(f => f.year === currYear).reduce((sum, f) => sum + (f.value || 0), 0);
                    return [prevTotal, currTotal];
                  }
                })(),
                units: (() => {
                  if (selectedWaterGroupIField) {
                    const obj = waterGroupIFields.find(f => f.id === selectedWaterGroupIField);
                    return [obj?.unit || unit, obj?.unit || unit];
                  } else {
                    return [unit, unit];
                  }
                })()
              }]}
              categories={[`${parseInt(waterFilter.year) - 1}`, `${waterFilter.year}`]}
              title={selectedWaterGroupIField
                ? waterGroupIFields.find(f => f.id === selectedWaterGroupIField)?.fieldName || "Selected Field"
                : "Total (All Fields)"}
              hover={selectedWaterGroupIField
                ? waterGroupIFields.find(f => f.id === selectedWaterGroupIField)?.fieldName || "Selected Field"
                : "Total (All Fields)"}
              unit={unit}
              yaxis={getYaxisLines()}
              height={350}
              max={getMaxYValue()}
              reportType={reportType}
              filterType={waterFilter.type}
              currentYear={parseInt(waterFilter.year)}
            />
          ) : reportType === "aqi" && group === "E" && aqiGroupEValue ? (
            <ColumnChart
              data={[{
                name: aqiGroupEValue.name,
                data: aqiGroupEValue.data,
                units: aqiGroupEValue.data.map(() => aqiGroupEValue.unit)
              }]}
              categories={aqiGroupEValue.categories}
              title={aqiGroupEValue.name}
              hover={aqiGroupEValue.name}
              unit={aqiGroupEValue.unit}
              yaxis={getYaxisLines()}
              height={350}
              max={getMaxYValue()}
              reportType={reportType}
              filterType={waterFilter.type}
              currentYear={parseInt(waterFilter.year)}
            />
          ) : reportType === "aqi" && group === "E" && waterFilter.type === "yearly_summary" && hasInitialFetch && aqiGroupEFields.length > 0 ? (
            <ColumnChart
              data={[{
                name: selectedAqiGroupEField
                  ? aqiGroupEFields.find(f => f.id === selectedAqiGroupEField)?.fieldName || "Selected Field"
                  : "Total (All Fields)",
                data: (() => {
                  const prevYear = parseInt(waterFilter.year) - 1;
                  const currYear = parseInt(waterFilter.year);
                  if (selectedAqiGroupEField) {
                    const prevObj = aqiGroupEFields.find(f => f.id === selectedAqiGroupEField && f.year === prevYear);
                    const currObj = aqiGroupEFields.find(f => f.id === selectedAqiGroupEField && f.year === currYear);
                    return [prevObj?.value || 0, currObj?.value || 0];
                  } else {
                    const prevTotal = aqiGroupEFields.filter(f => f.year === prevYear).reduce((sum, f) => sum + (f.value || 0), 0);
                    const currTotal = aqiGroupEFields.filter(f => f.year === currYear).reduce((sum, f) => sum + (f.value || 0), 0);
                    return [prevTotal, currTotal];
                  }
                })(),
                units: (() => {
                  if (selectedAqiGroupEField) {
                    const obj = aqiGroupEFields.find(f => f.id === selectedAqiGroupEField);
                    return [obj?.unit || unit, obj?.unit || unit];
                  } else {
                    return [unit, unit];
                  }
                })()
              }]}
              categories={[`${parseInt(waterFilter.year) - 1}`, `${waterFilter.year}`]}
              title={selectedAqiGroupEField
                ? aqiGroupEFields.find(f => f.id === selectedAqiGroupEField)?.fieldName || "Selected Field"
                : "Total (All Fields)"}
              hover={selectedAqiGroupEField
                ? aqiGroupEFields.find(f => f.id === selectedAqiGroupEField)?.fieldName || "Selected Field"
                : "Total (All Fields)"}
              unit={unit}
              yaxis={getYaxisLines()}
              height={350}
              max={getMaxYValue()}
              reportType={reportType}
              filterType={waterFilter.type}
              currentYear={parseInt(waterFilter.year)}
            />
          ) : (
            <ColumnChart
              data={
                data?.map((item) => ({
                  name: item.name,
                  data: Array.isArray(item.data)
                    ? item.data.map((d) => typeof d === "object" ? d.value : d)
                    : [],
                  units: Array.isArray(item.data)
                    ? item.data.map((d) => typeof d === "object" ? d.unit : null)
                    : [],
                })) || []
              }
              categories={Array.isArray(head) ? head : []}
              title={getCurrentTitle()}
              hover={getCurrentTitle()}
              unit={unit}
              yaxis={getYaxisLines()}
              height={350}
              max={getMaxYValue()}
              reportType={reportType}
              filterType={waterFilter.type}
              currentYear={parseInt(waterFilter.year)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardHistogram;